import { Alert, Grid } from '@mui/material'
import { useMemo } from 'react'
import {
  BooleanInput,
  maxLength,
  SelectInput,
  TextInput,
  TranslatableInputs,
  useLocale,
  useTranslate,
} from 'react-admin'
import { useWatch } from 'react-hook-form'
import { GridDevider } from '~/components'
import { nautixMissing } from '~/helpers'
import { boatCountryCodes, saleStatusChoices, vatStatusChoices } from '~/types/choices'

const blankToNull = (value: string) => (value?.trim() ? value.trim() : null)

/**
 * Whether the boat goes out with the feed, and if not, why: the same check the
 * generator makes (nautixMissing), said while the form is still open.
 */
export const NautixReadiness = () => {
  const translate = useTranslate()
  const [publish, boat_type, motorboat_type, model, asking_price, location_country] = useWatch({
    name: [
      'publish_nautix',
      'boat_type',
      'motorboat_type',
      'model',
      'asking_price',
      'location_country',
    ],
  })
  if (!publish) {
    return <Alert severity='info'>{translate('resources.boats.nautix.not_published')}</Alert>
  }
  const missing = nautixMissing({
    boat_type,
    motorboat_type,
    model,
    asking_price,
    location_country,
  })
  if (missing.length === 0) {
    return <Alert severity='success'>{translate('resources.boats.nautix.ready')}</Alert>
  }
  const fields = missing.map((key) => translate(`resources.boats.nautix.required.${key}`))
  return (
    <Alert severity='warning'>
      {translate('resources.boats.nautix.missing', { fields: fields.join(', ') })}
    </Alert>
  )
}

/**
 * What the portals get: the switch that releases a boat to the NautiX feed,
 * and the listing fields the CRM did not have before (docs/nautix/DATA-MAPPING.md).
 */
export const UpsertBoatPortals = () => {
  const locale = useLocale()
  const translate = useTranslate()
  const vatStatus = useWatch({ name: 'vat_status' })

  const countryChoices = useMemo(() => {
    let names: Intl.DisplayNames | null = null
    try {
      names = new Intl.DisplayNames([locale], { type: 'region' })
    } catch {
      // An engine without region names shows the codes.
    }
    return boatCountryCodes
      .map((id) => ({ id, name: names?.of(id) ?? id }))
      .sort((a, b) => a.name.localeCompare(b.name, locale))
  }, [locale])

  return (
    <>
      <GridDevider text='resources.boats.sections.portals' />
      <Grid size={{ md: 12 }}>
        <BooleanInput
          source='publish_nautix'
          label='resources.boats.fields.publish_nautix'
          helperText='resources.boats.fields.publish_nautix_hint'
        />
      </Grid>
      <Grid size={{ md: 12 }}>
        <NautixReadiness />
      </Grid>
      <Grid size={{ md: 8 }}>
        <TextInput
          source='subtitle'
          fullWidth
          parse={blankToNull}
          validate={[maxLength(60)]}
          label='resources.boats.fields.subtitle'
          helperText='resources.boats.fields.subtitle_hint'
        />
      </Grid>
      <Grid size={{ md: 4 }}>
        <TextInput
          source='external_reference'
          fullWidth
          parse={blankToNull}
          label='resources.boats.fields.external_reference'
          helperText='resources.boats.fields.external_reference_hint'
        />
      </Grid>
      <Grid size={{ md: 12 }}>
        <TranslatableInputs locales={['de', 'en']} defaultLocale='de'>
          <TextInput
            source='translated_marketing_description'
            multiline
            minRows={6}
            fullWidth
            label='resources.boats.fields.translated_marketing_description'
          />
        </TranslatableInputs>
      </Grid>
      <Grid size={{ md: 4 }}>
        <TextInput
          source='location_town'
          fullWidth
          parse={blankToNull}
          label='resources.boats.fields.location_town'
        />
      </Grid>
      <Grid size={{ md: 4 }}>
        <SelectInput
          source='location_country'
          choices={countryChoices}
          translateChoice={false}
          fullWidth
          label='resources.boats.fields.location_country'
        />
      </Grid>
      <Grid size={{ md: 4 }}>
        <TextInput
          source='location_state'
          fullWidth
          parse={(value: string) => blankToNull(value)?.toUpperCase() ?? null}
          label='resources.boats.fields.location_state'
          helperText='resources.boats.fields.location_state_hint'
        />
      </Grid>
      <Grid size={{ md: 4 }}>
        <SelectInput
          source='vat_status'
          choices={vatStatusChoices}
          fullWidth
          label='resources.boats.fields.vat_status'
        />
      </Grid>
      <Grid size={{ md: 4 }}>
        <TextInput
          source='vat_paid_area'
          fullWidth
          disabled={vatStatus !== 'paid'}
          parse={(value: string) => blankToNull(value)?.toUpperCase() ?? null}
          label='resources.boats.fields.vat_paid_area'
          helperText='resources.boats.fields.vat_paid_area_hint'
        />
      </Grid>
      <Grid size={{ md: 4 }}>
        <SelectInput
          source='sale_status'
          choices={saleStatusChoices}
          emptyText={translate('resources.boats.fields.sale_status_none')}
          fullWidth
          label='resources.boats.fields.sale_status'
        />
      </Grid>
    </>
  )
}

export default UpsertBoatPortals
